import firebase from '@modules/firebase';
import { logger } from '../logger';
import { getMessageForError } from './util';

type Provider = 'google.com' | 'microsoft.com' | 'apple.com' | 'oidc.oauth2proxy';

interface SignInWithRedirectArgs {
  email: string;
  providerId: Provider;
}

interface SendSignInLinkToEmailArgs {
  email: string;
  /** URL you want to redirect back to. */
  redirectTo: string;
}

function getOAuthProvider(providerId: Provider) {
  if (providerId === 'google.com') {
    return new firebase.auth.GoogleAuthProvider();
  }
  return new firebase.auth.OAuthProvider(providerId);
}

function isAuthError(error: unknown): error is firebase.auth.Error {
  return typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string';
}

class FirebaseAuthService {
  #log = logger.child({ class: 'FirebaseAuthService' });

  async createUserWithEmailAndPassword(email: string, password: string) {
    this.#log.info('Signing up with Firebase Auth.', { email, method: 'password' });

    try {
      const result = await firebase.auth().createUserWithEmailAndPassword(email, password);

      if (result.user) {
        this.#log.info('Successfully signed up with Firebase Auth.', { result, email, method: 'password' });
      } else {
        this.#log.info('Unsuccessful sign-up with Firebase Auth.', { result, email, method: 'password' });
      }

      return result;
    } catch (error) {
      this.#log.error('Error signing up with Firebase Auth.', { error, email, method: 'password' });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  async signInWithEmailAndPassword(email: string, password: string) {
    this.#log.info('Signing in with Firebase Auth.', { email, method: 'password' });

    try {
      const result = await firebase.auth().signInWithEmailAndPassword(email, password);

      if (result.user) {
        this.#log.info('Successful sign-in with Firebase Auth.', { result, email, method: 'password' });
      } else {
        this.#log.info('Unsuccessful sign-in with Firebase Auth.', { result, email, method: 'password' });
      }

      return result;
    } catch (error) {
      this.#log.error('Error signing in with Firebase Auth.', { error, email, method: 'password' });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  async signInWithCustomToken(token: string) {
    this.#log.info('Signing in with Firebase Auth.', { token, method: 'custom-token' });

    try {
      const result = await firebase.auth().signInWithCustomToken(token);

      if (result.user) {
        this.#log.info('Successful sign-in with Firebase Auth.', { result, method: 'custom-token' });
      } else {
        this.#log.info('Unsuccessful sign-in with Firebase Auth.', { result, method: 'custom-token' });
      }

      return result;
    } catch (error) {
      this.#log.error('Error signing in with Firebase Auth.', { error, method: 'custom-token' });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  async sendSignInLinkToEmail({ email, redirectTo }: SendSignInLinkToEmailArgs) {
    this.#log.info('Sending email sign-in link with Firebase Auth.', { email, redirectTo, method: 'link' });

    try {
      localStorage.setItem('emailForSignIn', email);

      await firebase.auth().sendSignInLinkToEmail(email, { url: redirectTo, handleCodeInApp: true });

      this.#log.info('Successfully sent email sign-in link with Firebase Auth.', {
        email,
        redirectTo,
        method: 'link',
      });
    } catch (error) {
      this.#log.error('Error sending email sign-in link with Firebase Auth.', {
        error,
        email,
        redirectTo,
        method: 'link',
      });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  public isSignInWithEmailLink(link: string) {
    this.#log.info('Checking if a link is an email sign-in link with Firebase Auth.', {
      link,
      method: 'link',
    });

    try {
      const isLink = firebase.auth().isSignInWithEmailLink(link);

      if (isLink) {
        this.#log.info('Link is an email sign-in link.', { link, method: 'link' });
      } else {
        this.#log.info('link is not an email sign-in link.', { link, method: 'link' });
      }

      return isLink;
    } catch (error) {
      this.#log.error('Error checking if a link is an email sign-in link with Firebase Auth.', {
        error,
        link,
        method: 'link',
      });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  async signInWithEmailLink(email: string, link: string) {
    this.#log.info('Signing in with Firebase Auth.', { email, link, method: 'link' });

    try {
      const result = await firebase.auth().signInWithEmailLink(email, link);

      if (result.user) {
        this.#log.info('Successful sign-in with Firebase Auth.', { result, email, link, method: 'link' });
      } else {
        this.#log.info('Unsuccessful sign-in with Firebase Auth.', { result, email, link, method: 'link' });
      }

      return result;
    } catch (error) {
      this.#log.error('Error signing in with Firebase Auth.', {
        error,
        email,
        link,
        method: 'link',
      });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  /**
   * Automatically redirects the user to the specified OAuth provider for authentication. This can be a
   * first-time sign up or a sign-in for an existing user.
   */
  async signInWithRedirect({ email, providerId }: SignInWithRedirectArgs) {
    this.#log.info('Signing in with Firebase Auth.', { email, method: 'oauth', providerId });

    try {
      const provider = getOAuthProvider(providerId);

      if (email) {
        provider.setCustomParameters({ login_hint: email });
      }

      localStorage.setItem('ssoMethod', providerId);

      // Used to set login loading state immediately upon return.
      localStorage.setItem('isSSOLogin', '1');

      await firebase.auth().signInWithRedirect(provider);

      this.#log.info('Successful sign-in with Firebase Auth.', { email, method: 'oauth', providerId });
    } catch (error) {
      this.#log.error('Error signing in with Firebase Auth.', { error, email, method: 'oauth', providerId });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  /**
   * Retrieves the signed-in user after a redirect to a social provider. For how a user is redirected to a
   * social provider, see `signInWithRedirect`.
   */
  async getRedirectResult() {
    // These values are set in the `FirebaseAuthService.signInWithRedirect` method.
    const isSsoLogin = localStorage.getItem('isSSOLogin');
    const ssoMethod = localStorage.getItem('ssoMethod');

    this.#log.info('Checking for redirect result with Firebase Auth.', {
      isSsoLogin,
      method: 'oauth',
      providerId: ssoMethod,
    });

    try {
      const result = await firebase.auth().getRedirectResult();

      if (result.user) {
        this.#log.info('Found user in redirect result with Firebase Auth.', {
          result,
          isSsoLogin,
          method: 'oauth',
          providerId: ssoMethod,
        });
      } else {
        this.#log.info('No redirect result found with Firebase Auth.', {
          result,
          isSsoLogin,
          method: 'oauth',
          providerId: ssoMethod,
        });
      }

      return result;
    } catch (error) {
      this.#log.error('Error getting redirect result with Firebase Auth.', {
        error,
        isSsoLogin,
        method: 'oauth',
        providerId: ssoMethod,
      });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  async signOut() {
    this.#log.info('Signing out of Firebase Auth.');

    try {
      await firebase.auth().signOut();

      this.#log.info('Successfully signed out of Firebase Auth.');
    } catch (error) {
      this.#log.error('Error signing out of Firebase Auth.', { error });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }
}

export const authService = new FirebaseAuthService();
